/**
 * Add Result Template – add/edit histopathology result template.
 * Migrated from admin4.0; uses useLocation().state.templateData for edit, useNavigate for redirect.
 * Result field uses ToolsField (rich text: bold, italic, underline, alignment).
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Paper,
  TextField,
} from "@mui/material";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import ToolsField from "../../Components/Common Components/ToolsField";
import { Colors } from "../../Styles/Colors";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import "../../Styles/Configuration.css";

// Allow letters, numbers, spaces for template name (max 50)
function isTemplateNameValid(value) {
  if (!value || typeof value !== "string") return false;
  return /^[a-zA-Z0-9\s]+$/.test(value) && value.length <= 50;
}

export default function AddResultTemplate() {
  const navigate = useNavigate();
  const location = useLocation();
  const templateData = location.state?.templateData || null;

  const [selectedServiceType, setSelectedServiceType] = useState(null);
  const [selectedServiceName, setSelectedServiceName] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [resultData, setResultData] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [postLoad, setPostLoad] = useState(false);
  const [HeaderList, setHeaderList] = useState([]);
  const [ServiceList, setServiceList] = useState([]);
  const [DupServiceList, setDupServiceList] = useState([]);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");

  const errorMessage = (msg) => {
    setIsErrorMsg(true);
    setIsErrorMsgText(msg);
  };
  const successMessage = (msg) => {
    setSuccessMsg(true);
    setSuccessMsgText(msg);
  };
  const msgClose = () => {
    setIsErrorMsg(false);
    setIsErrorMsgText("");
    setSuccessMsg(false);
    setSuccessMsgText("");
  };

  const getServiceConfigData = useCallback(() => {
    const params = new URLSearchParams({
      module_type: "HISTOPATHOLOGY",
      service_head_id: "",
      service_details_id: "",
      service_head: "",
      service_name: "",
      service_head_code: "",
      charge_type: "Normal",
    });
    RestAPIService.getAll(Serviceurls.NEW_DEPARTMENT_MASTER + "?" + params.toString())
      .then((res) => {
        if (res?.data?.status !== "success") return;
        const raw = res.data?.data || [];
        const mapped = raw.map((row) => {
          const headDetails = row.service_head_details ?? "";
          const serviceHead = headDetails.includes(" - ")
            ? headDetails.split(" - ").slice(1).join(" - ").trim()
            : headDetails || row.service_head;
          return { ...row, service_head: row.service_head ?? serviceHead };
        });
        const byHeadCode = new Map();
        mapped.forEach((item) => {
          const code = item.service_head_code ?? "";
          if (!byHeadCode.has(code))
            byHeadCode.set(code, { service_head: item.service_head, service_head_code: item.service_head_code });
        });
        setHeaderList(Array.from(byHeadCode.values()));
        setDupServiceList(mapped);
        setServiceList([]);
        if (templateData) {
          const head = Array.from(byHeadCode.values()).find(
            (h) => h.service_head === templateData?.service_head || h.service_head_code === templateData?.service_head_code
          );
          const name = mapped.find(
            (item) => item.service_name === templateData?.service_name && item.service_head === (head?.service_head ?? templateData?.service_head)
          );
          setSelectedServiceType(head || null);
          setSelectedServiceName(name || null);
          if (head) setServiceList(mapped.filter((item) => item.service_head_code === head.service_head_code));
        }
      })
      .catch((e) => errorMessage(e?.response?.data?.message || e?.message));
  }, [templateData]);

  useEffect(() => {
    getServiceConfigData();
  }, [getServiceConfigData]);

  useEffect(() => {
    if (!templateData) return;
    setTemplateName(templateData.template_name || "");
    setResultData(templateData.template_data || "");
    setSelectedId(templateData.id ?? null);
  }, [templateData]);

  const handleHeadChange = (event, value) => {
    setSelectedServiceType(value);
    const filtered = value ? DupServiceList.filter((item) => item?.service_head_code === value?.service_head_code) : [];
    setServiceList(filtered);
    setSelectedServiceName(null);
  };

  const checkValidation = (resultText) => {
    if (selectedServiceName == null || selectedServiceType == null) return "Please select Service Head and Service Name.";
    if (!templateName?.trim()) return "Please enter Template Name.";
    if (!isTemplateNameValid(templateName)) return "Template Name can only contain letters, numbers and spaces (max 50).";
    const text = (resultText ?? "").replace(/<[^>]*>/g, "").trim();
    if (!text) return "Please enter Result content.";
    return null;
  };

  const postNewTemplateData = (resultHtml) => {
    const resultText = resultHtml ?? resultData ?? "";
    const err = checkValidation(resultText);
    if (err) {
      errorMessage(err);
      return;
    }
    setPostLoad(true);
    const data = {
      service_head: selectedServiceType?.service_head ?? selectedServiceName?.service_head ?? "",
      service_name: selectedServiceName?.service_name ?? "",
      template_name: templateName.trim(),
      template_data: resultText.trim(),
    };
    if (selectedId) data.template_id = selectedId;

    RestAPIService.create(data, Serviceurls.HISTOPATHOLOGY_RESULT_TEMPLATE + "?module_type=HISTOPATHOLOGY")
      .then((res) => {
        if (res.data.status === "success") {
          successMessage(res.data.message);
          onClearHandler();
          setPostLoad(false);
          setTimeout(() => navigate("/HistopathologySetting/MasterConfiguration/ResultTemplate"), 800);
        }
      })
      .catch((e) => {
        setPostLoad(false);
        errorMessage(e?.response?.data?.message || e.message);
      });
  };

  const onClearHandler = () => {
    setSelectedServiceName(null);
    setSelectedServiceType(null);
    setTemplateName("");
    setResultData("");
    setSelectedId(null);
  };

  return (
    <Box component="div" sx={{ width: "100%", boxSizing: "border-box" }}>
      <Box component="div" className="rad_tst_container" sx={{ width: "100%", backgroundColor: Colors.Background, boxSizing: "border-box" }}>
        <Box component="div" className="rad_tst_tmp_header">
          <Paper component="div" className="rad_srv_tmp_pap" elevation={1}>
            <Box display="flex" flexDirection="row" alignItems="center" flexWrap="wrap" gap={1}>
              <Autocomplete
                options={HeaderList}
                clearIcon={false}
                value={selectedServiceType}
                getOptionLabel={(option) => (option?.service_head_code ? `${option.service_head_code} - ` : "") + (option?.service_head || "")}
                onChange={handleHeadChange}
                sx={{ width: "15vw" }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Service Head *" variant="outlined" size="small" />
                )}
              />
              <Autocomplete
                options={ServiceList}
                clearIcon={false}
                value={selectedServiceName}
                getOptionLabel={(option) => (option?.service_code ? `${option.service_code} - ` : "") + (option?.service_name || "")}
                onChange={(e, value) => setSelectedServiceName(value)}
                sx={{ width: "15vw" }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Service Name *" variant="outlined" size="small" />
                )}
              />
              <TextField
                sx={{ width: "15vw" }}
                inputProps={{ maxLength: 50 }}
                size="small"
                value={templateName}
                label="Template Name *"
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^[a-zA-Z0-9\s]*$/.test(v)) setTemplateName(v);
                }}
              />
            </Box>
          </Paper>
        </Box>

        <ToolsField
          Title="Result*"
          EditorRef={resultData}
          handlePost={postNewTemplateData}
          height="51.5vh"
          overallHeight="67vh"
          EditorHeight="53vh"
          addSpaceOnTab
          disableSave={postLoad}
        />
      </Box>

      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
    </Box>
  );
}
